import { Box, Button, DialogActions, DialogContent, DialogTitle, Drawer, FormControl, FormLabel, Grid, Input, ModalClose, Sheet, Textarea } from "@mui/joy";
import { Dialog } from "@mui/material";
import React from "react";
import { Util } from "../Helpers/Util";
import { Ticket } from "../Interfaces/Ticket";
import "./TicketDialog.css";

interface TicketDialogProps {
	ticketsId: string;
	close: Function;
}

export default function TicketDialog(props: TicketDialogProps) {
	const [ticket, setTicket] = React.useState<Ticket>();
	const [open, setOpen] = React.useState(false);
	const [isPrint, setIsPrint] = React.useState(false);
	const [pictures, setPictures] = React.useState<Array<string>>([]);
	const [openPicture, setOpenPicture] = React.useState("");
	const [signatureImageError, setSignatureImgError] = React.useState(false);

	React.useEffect(() => {
		setSignatureImgError(false);
	}, [ticket?.tickets_id, props.ticketsId]);

	React.useEffect(() => {
		setOpen(true);
		getTicket();
	}, []);

	const getTicket = () => {
		fetch("/Ticket/GetTicketData", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ ticketsId: props.ticketsId }),
		})
			.then((x) => x.json())
			.then((y) => {
				console.log(y);
				setTicket(y[0] as Ticket);
				setPictures(Array.from(new Set<string>(y.filter((x: any) => x.tickets_pictures_link_id).map((x: any) => x.tickets_pictures_link_id))));
			});
	};

	const printTicket = () => {
		setIsPrint(true);
		window.setTimeout(() => {
			window.print();
			window.setTimeout(() => {
				setIsPrint(false);
			}, 200);
		}, 1000);
	};

	const close = () => {
		props.close();
	};

	const sizes = {
		sm: 12,
		md: 6,
	};

	const threeSizes = {
		sm: 12,
		md: 6,
		lg: 4,
	};

	const fieldSizes = isPrint ? { xs: 4 } : threeSizes;
	const fullWidth = isPrint ? { xs: 12 } : { sm: 12 };

	const printBorderColor = "#333";

	const printBorderSx = isPrint
		? {
				boxShadow: "none",
				borderRadius: 0,
				"--Input-radius": 0,
				"--Sheet-radius": 0,
				"--Input-borderColor": printBorderColor,
				border: `1px solid ${printBorderColor}`,
			}
		: {};

	const inputSx = {
		border: "1px solid",
		borderColor: isPrint ? printBorderColor : "neutral.outlinedBorder",
		backgroundColor: isPrint ? "#fff" : "background.surface",
		color: isPrint ? "#000" : undefined,
		...(isPrint
			? {
					"--Input-minHeight": "1.5rem",
					"--Input-borderColor": printBorderColor,
					"--Input-focusedThickness": "1px",
					py: 0.125,
				}
			: {}),
		...printBorderSx,
	};

	const textBlockSx = {
		p: isPrint ? 0.5 : 1,
		overflow: "visible",
		whiteSpace: "pre-wrap",
		...(isPrint ? { borderRadius: 0, boxShadow: "none", border: `1px solid ${printBorderColor}` } : { borderRadius: 0, boxShadow: "md" }),
	};

	const renderField = (label: string, value: string | null | undefined) => {
		const displayValue = value ?? "";
		return (
			<FormControl>
				<FormLabel>{label}</FormLabel>
				<Input disabled={!isPrint} readOnly={isPrint} value={displayValue} sx={inputSx} />
			</FormControl>
		);
	};

	const renderPictureDialog = () => {
		return (
			<Drawer onClose={() => setOpenPicture("")} open={!Util.isNullOrEmpty(openPicture)} size="lg">
				<ModalClose onClick={() => setOpenPicture("")} />
				<DialogTitle>Ticket Picture</DialogTitle>
				<DialogContent sx={{ padding: 2 }}>
					<img src={"Mobile/GetTicketPicture/" + openPicture} style={{ maxHeight: "100%", maxWidth: "100%", height: "auto", width: "auto" }} />
				</DialogContent>
			</Drawer>
		);
	};

	const render = () => {
		return (
			<>
				{!isPrint && <ModalClose onClick={() => setOpen(false)} />}
				<DialogTitle sx={isPrint ? { py: 0.75, px: 1.5 } : undefined}>
					Ticket #{ticket?.ticket_number} - {ticket?.building_name}
				</DialogTitle>
				<DialogContent sx={{ padding: isPrint ? 1 : 2, ...(isPrint ? { overflow: "visible", maxHeight: "none" } : {}) }}>
					{ticket && (
						<Grid container spacing={isPrint ? 0.5 : 2} sx={isPrint ? { width: "100%", overflow: "visible" } : { overflowX: "hidden" }}>
							<Grid xs={12}>
								<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Ticket Information</FormLabel>
							</Grid>
							<Grid {...fieldSizes}>{renderField("Date Entered", Util.Formatter(ticket.date_entered, "D") as string)}</Grid>
							<Grid {...fieldSizes}>{renderField("Elevator", ticket.serial_number + " - " + ticket.elevator_type)}</Grid>
							<Grid {...fieldSizes}>{renderField("Mechanic", ticket.mechanic)}</Grid>
							<Grid {...fieldSizes}>{renderField("Ticket Type", ticket.ticket_type)}</Grid>
							{ticket.ticket_type?.includes("Maintenance") && (
								<>
									<Grid xs={12}>
										<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Elevator Maintenance</FormLabel>
									</Grid>
									<Grid {...fieldSizes}>{renderField("Alarm Working?", Util.Formatter(ticket.alarm, "L") as string)}</Grid>
									<Grid {...fieldSizes}>{renderField("Phone Working?", Util.Formatter(ticket.emergency_phone, "L") as string)}</Grid>
									<Grid {...fieldSizes}>{renderField("Light Working?", Util.Formatter(ticket.emergency_light, "L") as string)}</Grid>
								</>
							)}
							<Grid {...fullWidth}>
								<FormControl>
									<FormLabel>Work Performed</FormLabel>
									{isPrint ? (
										<Sheet className="ticket-print-compact-text" sx={textBlockSx} variant="outlined" component="div">
											{ticket.work_performed}
										</Sheet>
									) : (
										<Textarea minRows={5} sx={{ overflowY: "visible" }} disabled={true} value={ticket.work_performed} />
									)}
								</FormControl>
							</Grid>

							{(!isPrint || !Util.isNullOrEmpty(ticket.recommendation)) && (
								<Grid {...fullWidth}>
									<FormControl>
										<FormLabel>Recommendations</FormLabel>
										{isPrint ? (
											<Sheet className="ticket-print-compact-text" sx={textBlockSx} variant="outlined" component="div">
												{ticket.recommendation}
											</Sheet>
										) : (
											<Textarea minRows={5} sx={{ overflowY: "visible" }} disabled={true} value={ticket.recommendation} />
										)}
									</FormControl>
								</Grid>
							)}
							<Grid {...fieldSizes}>{renderField("Work Completed?", Util.Formatter(ticket.work_completed, "L") as string)}</Grid>
							{!isPrint && (
								<Grid {...fieldSizes}>
									<FormControl>
										<FormLabel>Status</FormLabel>
										<Input disabled={true} value={ticket.submitted || ticket.no_signature ? "Submitted" : "Pending"} sx={inputSx} />
									</FormControl>
								</Grid>
							)}
							<Grid {...fieldSizes}>{renderField("Signed By", ticket.no_signature ? "Noone was present to sign." : ticket.signature_name)}</Grid>
							<Grid xs={12}>
								{ticket.no_signature || signatureImageError ? (
									<FormLabel sx={{ typography: "title-md" }}>No Signature on File</FormLabel>
								) : (
									<img
										alt=""
										style={{ maxHeight: isPrint ? "90px" : "120px" }}
										src={"Ticket/GetTicketSignature/" + ticket.tickets_id}
										onError={() => setSignatureImgError(true)}
									/>
								)}
							</Grid>
							{pictures.length > 0 && (
								<>
									<Grid xs={12}>
										<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Pictures</FormLabel>
									</Grid>
									{pictures.map((x) => {
										return (
											<Grid {...fieldSizes} key={x}>
												<img src={"Mobile/GetTicketPicture/" + x} style={{ maxHeight: 80, maxWidth: 80, height: "auto", width: "auto", cursor: "pointer" }} onClick={() => setOpenPicture(x)} />
											</Grid>
										);
									})}
								</>
							)}

							{isPrint && (
								<Grid xs={12} className="ticket-print-legal" container spacing={0.25}>
									<Grid sx={{ pageBreakBefore: "always", justifyContent: "center" }} xs={12}>
										<FormLabel sx={{ typography: "h2", fontWeight: "bold", justifyContent: "center" }}>Legal</FormLabel>
									</Grid>
									<Grid xs={12} sx={{ justifyContent: "center" }}>
										<b>Dynamic Elevator Control Corp.</b> assumes no responsibility for any part of the elevator equipment except the upon which work has been done under this contract. No work service examination or liability on our part other than that specifically mentioned herein is included or intended. It is agreed that <b>Dynamic Elevator Control Corp.</b> does not assume possession or control of any part of the equipment and that such remains yours exclusively as the owner lessor, lessee or manager thereof.
									</Grid>
									<Grid xs={12} sx={{ justifyContent: "center" }}>
										<b>Dynamic Elevator Control Corp.</b> shall not be held responsible for any loss, damage, injury, delay or detention of persons or property caused by acts of government, civil or military authorities, war, civil commotion, theft, vandalism, labor troubles, explosion, fire, power failure, water damage, storms, lighting, natural or public catastrophe, acts of god, or by any other cause which is unavailable or beyond our reasonable control, and, in any event, we shall not be liable for consequential damages. It is agreed that in consideration of the
										services enumerated herein that nothing in this agreement shall be construed to mean that <b>Dynamic Elevator Control Corp.</b> or its representatives assumes any liability whatsoever on account of accidents, injuries, delay or detention to persons or property. The purchaser shall remain solely liable for any and all actions or claims for damages to persons property while riding on, being in or about the elevator or the associated areas regardless of whether such actions result from the use, operation, maintenance or condition of the elevator, machine
										rooms, hatchway or any of their components parts and the Purchaser does hereby further unconditionally agree to indemnify and hold harmless <b>Dynamic Elevator Control Corp.</b> and its officers, directors and employees from any and all claims as enumerated above, including, but not limited to any demands, judgments, awards liabilities, costs, attorney's fees or any other damages on account of any such actions or claims regardless of the cause of said actions and regardless of any negligence upon the part of <b>Dynamic Elevator Control Corp.</b>
									</Grid>
									<Grid xs={12} sx={{ justifyContent: "center" }}>
										Purchaser shall shut down the equipment immediately upon manifestation of any irregularities in operation or appearance in the equipment, notify <b>Dynamic Elevator Control Corp.</b> at once and keep equipment shut down until the completion of the repairs and further the Purchase shall keep the equipment under continued surveillance by competent personal to detect such irregularities. The Purchaser shall give <b>Dynamic Elevator Control Corp.</b> notice within ten days after occurrence of any accident in or about the elevator.{" "}
									</Grid>
									<Grid xs={12} sx={{ justifyContent: "center" }}>
										Purchaser agrees to pay for work performed upon presentation of invoice.
									</Grid>
									<Grid xs={12} sx={{ justifyContent: "center" }}>
										Nonpayment of any sum under this agreement shall be considered a material breach thereof. In the event of a breach of this agreement or the failure to pay any sum due under this agreement within thirty (30) days from the billing date. <b>Dynamic Elevator Control Corp.</b> may, at its option, declare all sums due or to become due under this agreement due payable in full as and for agreed and liquidate damages, and not as a penalty, and until the same are paid. <b>Dynamic Elevator Control Corp.</b> shall be discharged and released from any obligation
										and/or liability under the terms of this agreement.
									</Grid>
									<Grid xs={12} sx={{ justifyContent: "center" }}>
										In the event any attorney is engaged to enforce construe or defend any of the terms and conditions of this agreement or to collect any payment or lost profits due here under either with or without suit, the Purchaser agrees to pay all attorney's fees and costs incurred by <b>Dynamic Elevator Control Corp.</b>{" "}
									</Grid>
									<Grid xs={12} sx={{ justifyContent: "center" }}>
										The Purchaser does hereby waive trial by jury and does further hereby consent to the venue of any proceeding or lawsuit under this agreement to be in the County of Broward and State of Florida other county of <b>Dynamic Elevator Control Corp.</b> choice.{" "}
									</Grid>
									<Grid xs={12} sx={{ justifyContent: "center" }}>
										No agent or employee shall have the authority to waive or modify any of the above terms of this agreement.
									</Grid>
								</Grid>
							)}
						</Grid>
					)}
				</DialogContent>
				{!isPrint && (
					<DialogActions>
						<Box sx={{ padding: 2 }}>
							<Button size="lg" color="primary" onClick={close} sx={{ mx: 1 }}>
								Close
							</Button>
							<Button size="lg" color="primary" onClick={printTicket} sx={{ mx: 1 }}>
								Print
							</Button>
						</Box>
					</DialogActions>
				)}
			</>
		);
	};

	return (
		<>
			{!isPrint ? (
				<>
					<Drawer onClose={() => props.close()} anchor="right" open={open} size="lg">
						{render()}
					</Drawer>
					{renderPictureDialog()}
				</>
			) : (
				<Dialog
					fullScreen
					open={open}
					className="ticket-print"
					PaperProps={{ sx: { overflow: "visible", maxHeight: "none" } }}
					sx={{ "& .MuiDialog-container": { overflow: "visible", alignItems: "flex-start" } }}
				>
					{render()}
				</Dialog>
			)}
		</>
	);
}
